import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  ScrollView,
  Dimensions,
  ViewStyle,
} from 'react-native';
import {
  useSharedValue,
  withTiming,
  runOnJS,
  Easing,
} from 'react-native-reanimated';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import ToastMessage from 'react-native-toast-message';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { toastConfig } from '@/components/common/ToastConfig';
import { AnimatedBackdrop } from './AnimatedBackdrop';
import { DialogContent, type DialogSize } from './DialogContent';
import { SheetContent } from './SheetContent';
import { FullscreenContent } from './FullscreenContent';
import { ModalHeader } from './ModalHeader';

export type ModalVariant = 'dialog' | 'sheet' | 'fullscreen';

export interface UnifiedModalProps {
  visible: boolean;
  onClose: () => void;
  variant: ModalVariant;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
  centerTitle?: boolean;
  headerRight?: React.ReactNode;
  headerStyle?: ViewStyle;
  footer?: React.ReactNode;
  footerStyle?: ViewStyle;
  avoidKeyboard?: boolean;
  scrollable?: boolean;
  dismissOnBackdrop?: boolean;
  dialogSize?: DialogSize;
  /** Numeric override for dialog maxWidth (in px). Takes precedence over `dialogSize`. */
  dialogMaxWidth?: number;
  enableGestureDismiss?: boolean;
  contentContainerStyle?: ViewStyle;
  /** Override onRequestClose (Android back). Defaults to onClose. Set to undefined to prevent. */
  onRequestClose?: (() => void) | undefined;
  /** Skip the entry animation (useful when React Navigation already provides a transition). */
  animateEntry?: boolean;
  /** On Android, adds bottom-inset buffer when keyboard is visible to compensate for
   *  manufacturer keyboard toolbars (e.g. Samsung clipboard/voice bar) that may not be
   *  included in keyboard event coordinates. Only needed for screens with inputs at the
   *  very bottom (e.g. chat). */
  compensateKeyboardToolbar?: boolean;
}

const SCREEN_HEIGHT = Dimensions.get('window').height;

const BACKDROP_OPACITY = 0.88;

const EASE_OUT = Easing.out(Easing.cubic);
const ENTRY_DURATION = 250;
const EXIT_DURATION = 180;

export const UnifiedModal: React.FC<UnifiedModalProps> = ({
  visible,
  onClose,
  variant,
  children,
  title,
  subtitle,
  showHeader = true,
  centerTitle = false,
  headerRight,
  headerStyle,
  footer,
  footerStyle,
  avoidKeyboard = false,
  scrollable = false,
  dismissOnBackdrop = true,
  dialogSize = 'default',
  dialogMaxWidth,
  enableGestureDismiss = true,
  contentContainerStyle,
  onRequestClose,
  animateEntry = true,
  compensateKeyboardToolbar = false,
}) => {
  const requestClose = onRequestClose !== undefined ? onRequestClose : onClose;
  const { colors } = useThemeColors();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const insets = useSafeAreaInsets();

  const [mounted, setMounted] = useState(!animateEntry && visible);

  // Shared animation values -- start at final state when skipping entry animation
  const backdropOpacity = useSharedValue(animateEntry ? 0 : BACKDROP_OPACITY);
  const dialogScale = useSharedValue(animateEntry ? 0.96 : 1);
  const contentOpacity = useSharedValue(animateEntry ? 0 : 1);
  const translateY = useSharedValue(animateEntry ? SCREEN_HEIGHT : 0);

  const isAndroid = Platform.OS === 'android';
  const isEmbedded = !animateEntry;

  const resolvedVariant: 'dialog' | 'sheet' | 'fullscreen' =
    variant === 'sheet' && isDesktop ? 'dialog' : variant;

  const fullscreenStandalone = resolvedVariant === 'fullscreen' && !isEmbedded;
  // Always use initialWindowMetrics fallback — on Android with edge-to-edge (RN 0.76+),
  // useSafeAreaInsets() can return 0 for embedded screens before native measurement completes.
  const bottomInset = Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0);

  // Manual keyboard height tracking — used for paddingBottom adjustment on both platforms.
  // For standalone modals (inside RN <Modal>), measures the wrapper's actual screen
  // position via measureInWindow to calculate exact keyboard overlap, avoiding
  // coordinate mismatches between the Modal window and Dimensions.get('window').
  // For Android embedded screens, measureInWindow self-corrects for partial adjustResize.
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const wrapperRef = useRef<View>(null);

  useEffect(() => {
    if (!avoidKeyboard || isDesktop) return;
    const showEvent = isAndroid ? 'keyboardDidShow' : 'keyboardWillShow';
    const hideEvent = isAndroid ? 'keyboardDidHide' : 'keyboardWillHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      setKeyboardVisible(true);
      const keyboardTop = e.endCoordinates.screenY;
      if (wrapperRef.current) {
        wrapperRef.current.measureInWindow((_x, y, _w, h) => {
          setKeyboardHeight(Math.max(0, y + h - keyboardTop));
        });
      } else {
        setKeyboardHeight(e.endCoordinates.height);
      }
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardVisible(false);
      setKeyboardHeight(0);
    });
    return () => { showSub.remove(); hideSub.remove(); };
  }, [avoidKeyboard, isAndroid, isDesktop]);

  const runEntryAnimation = useCallback(() => {
    const config = { duration: ENTRY_DURATION, easing: EASE_OUT };
    backdropOpacity.value = withTiming(BACKDROP_OPACITY, config);

    if (resolvedVariant === 'dialog') {
      dialogScale.value = withTiming(1, config);
      contentOpacity.value = withTiming(1, config);
    } else {
      translateY.value = withTiming(0, config);
    }
  }, [resolvedVariant, backdropOpacity, dialogScale, contentOpacity, translateY]);

  const runExitAnimation = useCallback(
    (onComplete: () => void) => {
      const config = { duration: EXIT_DURATION, easing: EASE_OUT };
      backdropOpacity.value = withTiming(0, config);

      if (resolvedVariant === 'dialog') {
        dialogScale.value = withTiming(0.98, config);
        contentOpacity.value = withTiming(0, config, () => {
          runOnJS(onComplete)();
        });
      } else {
        translateY.value = withTiming(SCREEN_HEIGHT, config, () => {
          runOnJS(onComplete)();
        });
      }
    },
    [resolvedVariant, backdropOpacity, dialogScale, contentOpacity, translateY],
  );

  const resetValues = useCallback(() => {
    backdropOpacity.value = 0;
    dialogScale.value = 0.96;
    contentOpacity.value = 0;
    translateY.value = SCREEN_HEIGHT;
  }, [backdropOpacity, dialogScale, contentOpacity, translateY]);

  const setFinalValues = useCallback(() => {
    backdropOpacity.value = BACKDROP_OPACITY;
    if (resolvedVariant === 'dialog') {
      dialogScale.value = 1;
      contentOpacity.value = 1;
    } else {
      translateY.value = 0;
    }
  }, [resolvedVariant, backdropOpacity, dialogScale, contentOpacity, translateY]);

  useEffect(() => {
    if (visible) {
      if (animateEntry) {
        resetValues();
      } else {
        setFinalValues();
      }
      setMounted(true);
    } else if (mounted) {
      runExitAnimation(() => {
        setMounted(false);
        resetValues();
      });
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger entry after mount renders the Modal
  useEffect(() => {
    if (mounted && visible) {
      if (!animateEntry) return;
      // Small delay so the RN Modal is actually on screen before we animate
      const timer = requestAnimationFrame(() => {
        runEntryAnimation();
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackdropPress = useCallback(() => {
    if (dismissOnBackdrop) onClose();
  }, [dismissOnBackdrop, onClose]);

  const handleSheetDismiss = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!mounted) return null;

  const headerElement = showHeader ? (
    <ModalHeader
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      centerTitle={centerTitle}
      headerRight={headerRight}
      style={headerStyle}
    />
  ) : null;

  // Footer paddingBottom:
  // - Desktop: fixed 24 for visual breathing room.
  // - Sheet/dialog: 16 only — the SheetContent/DialogContent container already
  //   adds safe-area paddingBottom. Adding it again here doubles the empty space
  //   below the buttons (visible especially on devices with a home indicator and
  //   when the keyboard is open).
  // - Fullscreen standalone: collapses to 16 when keyboard up, else bottomInset + 30.
  // - Embedded (other): standard safe-area clearance.
  const footerPaddingBottom = isDesktop
    ? 24
    : resolvedVariant === 'sheet' || resolvedVariant === 'dialog'
      ? 16
      : fullscreenStandalone
        ? keyboardHeight > 0
          ? 16
          : bottomInset + 30
        : Math.max(insets.bottom, 16);

  const footerElement = footer ? (
    <View
      style={[
        styles.footer,
        { borderTopColor: colors.separator, paddingBottom: footerPaddingBottom },
        footerStyle,
      ]}
    >
      {footer}
    </View>
  ) : null;

  const bodyContent = scrollable ? (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {children}
    </ScrollView>
  ) : (
    <View style={[
      resolvedVariant === 'fullscreen' && styles.body,
      (resolvedVariant === 'dialog' || resolvedVariant === 'sheet') && styles.boundedBody,
      contentContainerStyle,
    ]}>{children}</View>
  );

  const innerContent = (
    <>
      {headerElement}
      {bodyContent}
      {footerElement}
    </>
  );

  // When compensateKeyboardToolbar is enabled on Android fullscreen embedded, the
  // keyboard spacer takes over bottom safe-area duties from FullscreenContent's
  // paddingBottom and adds a bottomInset buffer when the keyboard is visible to
  // compensate for manufacturer keyboard toolbars (e.g. Samsung clipboard/voice bar)
  // that may not be included in keyboard event coordinates or adjustResize insets.
  const useAndroidKeyboardSpacer = isAndroid && isEmbedded && resolvedVariant === 'fullscreen' && compensateKeyboardToolbar;

  const wrapWithKeyboard = (content: React.ReactNode) => {
    if (!avoidKeyboard) return <>{content}</>;

    // Standalone dialog/sheet: avoid flex:1 wrapper which collapses height in
    // Yoga when parent is content-sized. On iOS the Modal-level KAV handles
    // keyboard avoidance; on Android adjustResize handles it for these variants.
    if (!isEmbedded && resolvedVariant !== 'fullscreen') {
      return <>{content}</>;
    }

    // iOS standalone fullscreen: KAV at the Modal level handles it.
    if (!isAndroid && !isEmbedded) {
      return <>{content}</>;
    }

    // Android fullscreen embedded: always render spacer that handles both bottom
    // safe area (replacing FullscreenContent paddingBottom) and keyboard avoidance
    // with a bottomInset buffer for manufacturer keyboard toolbars.
    if (useAndroidKeyboardSpacer) {
      const spacerHeight = keyboardVisible
        ? keyboardHeight + bottomInset
        : bottomInset;
      return (
        <View ref={wrapperRef} style={styles.flex}>
          <View style={styles.flex}>{content}</View>
          <View style={{ height: spacerHeight }} />
        </View>
      );
    }

    // Android (other variants) + iOS embedded: manual spacer approach
    // Uses measureInWindow to calculate exact keyboard overlap, which self-corrects
    // for any partial adjustResize behavior on Android embedded screens.
    return (
      <View ref={wrapperRef} style={styles.flex}>
        <View style={styles.flex}>{content}</View>
        {keyboardHeight > 0 && <View style={{ height: keyboardHeight }} />}
      </View>
    );
  };

  const renderVariantContent = () => {
    switch (resolvedVariant) {
      case 'dialog':
        return (
          <DialogContent
            scale={dialogScale}
            contentOpacity={contentOpacity}
            size={dialogSize}
            maxWidthOverride={dialogMaxWidth}
          >
            {wrapWithKeyboard(innerContent)}
          </DialogContent>
        );

      case 'sheet':
        return (
          <SheetContent
            translateY={translateY}
            backdropOpacity={backdropOpacity}
            maxBackdropOpacity={BACKDROP_OPACITY}
            onDismiss={handleSheetDismiss}
            enableGestureDismiss={enableGestureDismiss}
            keyboardVisible={keyboardVisible}
          >
            {wrapWithKeyboard(innerContent)}
          </SheetContent>
        );

      case 'fullscreen':
        return (
          <FullscreenContent translateY={translateY} embedded={!animateEntry} avoidKeyboard={useAndroidKeyboardSpacer}>
            {wrapWithKeyboard(innerContent)}
          </FullscreenContent>
        );
    }
  };

  if (!animateEntry) {
    return (
      <View style={styles.root}>
        {renderVariantContent()}
      </View>
    );
  }

  // Skip KAV for Android fullscreen standalone — Android Modal adjustResize already
  // resizes the window when the keyboard opens; adding KAV behavior="height" on top
  // double-compensates and pushes the footer up excessively. The manual spacer in
  // wrapWithKeyboard (via measureInWindow) handles any remaining overlap accurately.
  const useKeyboardAvoidingView = avoidKeyboard && !isDesktop && !(isAndroid && fullscreenStandalone);

  const modalBody = (
    <>
      <AnimatedBackdrop
        opacity={backdropOpacity}
        onPress={handleBackdropPress}
        enableBlur={resolvedVariant !== 'fullscreen'}
      />
      {renderVariantContent()}
    </>
  );

  // Keyboard bleed strip: covers the area behind the keyboard with the sheet's
  // background color. iOS keyboards have ~12-16px rounded top corners — at those
  // corners, the keyboard's visible pixels curve inward, exposing the modal
  // surface beneath. Without this strip, those wedges reveal the transparent
  // Modal background. With it, the wedges reveal a color matching the sheet,
  // so the seam between sheet bottom and keyboard top is invisible.
  // Sheet variant only — dialog/fullscreen don't anchor flush to the keyboard.
  const showKeyboardBleed =
    avoidKeyboard &&
    !isDesktop &&
    resolvedVariant === 'sheet' &&
    keyboardHeight > 0;

  const keyboardBleedStrip = showKeyboardBleed ? (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: keyboardHeight,
        backgroundColor: colors.bg.modal,
      }}
    />
  ) : null;

  return (
    <Modal
      visible={mounted}
      animationType="none"
      transparent={true}
      statusBarTranslucent
      onRequestClose={requestClose}
    >
      <View style={styles.root}>
        {useKeyboardAvoidingView ? (
          <KeyboardAvoidingView style={styles.root} behavior={isAndroid ? 'height' : 'padding'}>
            {modalBody}
          </KeyboardAvoidingView>
        ) : (
          <View style={styles.root}>
            {modalBody}
          </View>
        )}
        {/* Rendered AFTER the KAV so it sits above the backdrop in z-order.
            The backdrop uses StyleSheet.absoluteFill and would otherwise paint
            its dim color over this strip. The strip's y range [keyboardTop,
            screenBottom] never overlaps the sheet (whose bottom is at
            keyboardTop), so painting on top here only affects the area behind
            the keyboard — exactly the wedge gaps we want to cover. */}
        {keyboardBleedStrip}
        {/* RN's native <Modal> renders in a separate native window above the RN
            root view, hiding the app-level Toast. Mount a second instance here:
            react-native-toast-message is a singleton whose ref repoints to the
            most recently mounted instance, so calls automatically route to this
            one while the modal is open. Web uses a portal at the provider level
            (see ToastContext.tsx), so no inline toast is needed there. */}
        {Platform.OS !== 'web' && (
          <ToastMessage config={toastConfig} topOffset={60} />
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  flex: {
    flex: 1,
  },
  body: {
    flex: 1,
  },
  // Dialog/sheet body: shrinks below its natural content size when the container
  // hits maxHeight (dialog: 85%, sheet: 90%), giving an internal ScrollView a
  // bounded parent so it can scroll. Critical when the keyboard opens and shrinks
  // the available area further — without this, content overflows and the bottom
  // (footer + last inputs) gets clipped behind the keyboard.
  boundedBody: {
    flexShrink: 1,
    minHeight: 0,
  },
  scrollView: {
    flexGrow: 1,
    flexShrink: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  footer: {
    borderTopWidth: 1,
    paddingHorizontal: 24,
    paddingTop: 16,
  },
});
